import { LitElement, html } from 'lit';
import { Task } from '@lit/task';

class SearchFaqPages extends LitElement {
  // Properties hold the state of the component.
  static properties = {
    input: '',
  };

  constructor() {
    super();
    this.parsedData = JSON.parse(
      this.querySelector('script[type="application/json"]')?.innerHTML
    ) || {
      translations: {
        input_placeholder: 'Search',
        clear: 'Clear',
      },
    };
  }

  /**
   * handleInput
   * @description Handle the input event for the predictive search.
   * @param {Object} event
   */
  handleInput(event) {
    event.preventDefault();
    this.input = event.currentTarget.value;
    if (this.input !== '') {
      this._fetchSearchResults.run(event.currentTarget.value);
    }
  }

  clearInput() {
    this.input = '';
    // this.querySelector('input[name="q"]').focus();
  }

  /**
   * _fetchSearchResults
   * @description Fetch data from the predictive search api using Lit Task for async render updates.
   * @param {String} searchTerm
   */
  _fetchSearchResults = new Task(this, {
    task: async (input, { signal }) => {
      const response = await fetch(
        `${routes.predictive_search_url}?q=${input}&resources[type]=page&resources[limit]=10&resources[limit_scope]=each`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', Accept: `application/json` },
          signal,
        }
      );

      if (!response.ok) {
        throw new Error(response.status);
      }
      const data = await response.json();
      return data;
    },
  });

  search = html``;

  render() {
    return html`
      <div class="">
        <form @submit="${this.handleInput}" class="relative">
          <button type="submit" class="absolute left-2 top-1/2 w-6 -translate-y-1/2 p-1">
            <svg
              class="icon--search icon"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              role="presentation"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M21 21L15 15L21 21ZM17 10C17 10.9193 16.8189 11.8295 16.4672 12.6788C16.1154 13.5281 15.5998 14.2997 14.9497 14.9497C14.2997 15.5998 13.5281 16.1154 12.6788 16.4672C11.8295 16.8189 10.9193 17 10 17C9.08075 17 8.1705 16.8189 7.32122 16.4672C6.47194 16.1154 5.70026 15.5998 5.05025 14.9497C4.40024 14.2997 3.88463 13.5281 3.53284 12.6788C3.18106 11.8295 3 10.9193 3 10C3 8.14348 3.7375 6.36301 5.05025 5.05025C6.36301 3.7375 8.14348 3 10 3C11.8565 3 13.637 3.7375 14.9497 5.05025C16.2625 6.36301 17 8.14348 17 10Z"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
            </svg>
            <span class="sr-only">${this.parsedData.translations.search}</span>
          </button>
          <input
            type="text"
            name="q"
            @input="${this.handleInput}"
            .value="${this.input || null}"
            placeholder="${this.parsedData.translations.input_placeholder}"
            class="field__input block w-full border border-primary bg-white py-3 pl-8 text-sm leading-5"
          />
          ${this.input?.length > 0
            ? html`
                <button
                  type="button"
                  @click="${this.clearInput}"
                  class="absolute right-4 top-1/2 block -translate-y-1/2 text-xs leading-5 hover:text-primary"
                >
                  ${this.parsedData.translations.clear}
                </button>
              `
            : null}
        </form>
      </div>
      ${this.input?.length > 0
        ? html`
            ${this._fetchSearchResults.render({
              pending: () => {
                return html`<p class="mt-10 text-xs leading-4 text-dark-gray">Loading...</p>`;
              },
              complete: (data) => {
                return html`
                  <p class="mb-6 mt-10 text-xs leading-4 text-dark-gray">
                    ${data.resources?.results?.pages?.length}
                    ${data.resources?.results?.pages?.length > 1
                      ? html`results for`
                      : html`result for`}
                    "${this.input}"
                  </p>

                  ${data.resources?.results?.pages?.length > 0
                    ? html` <ul class="block">
                        ${data.resources?.results?.pages?.map((page) => {
                          return html`<li class="">
                            <a
                              href="${page.url}"
                              class="relative block w-full border-b border-b-dark-gray py-3 pr-8 text-lg leading-6 md:text-xl md:leading-7"
                            >
                              ${page.title}
                              <svg
                                class="icon--chevron-right icon absolute right-0 top-1/2 w-4 -translate-y-1/2 transform"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                                role="presentation"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  d="M6.00003 12L10 8L6.00003 4"
                                  stroke="currentColor"
                                  stroke-linecap="square"
                                  stroke-linejoin="round"
                                ></path>
                              </svg>
                            </a>
                          </li>`;
                        })}
                      </ul>`
                    : html`<p class="mt-10 text-xs leading-4 text-dark-gray">No results found</p>`}
                `;
              },
              error: (e) => {
                return html`<p class="mt-10 text-xs leading-4 text-dark-gray">${e}</p>`;
              },
            })}
          `
        : null}
    `;
  }

  createRenderRoot() {
    return this;
  }
}

customElements.define('search-faq', SearchFaqPages);
